import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Icon,
  Typography,
} from '@mui/material';
import { SparkEllipsisTypography, SparkImageLoader } from 'sharedComponents/UI';
import {
  ExcelFileIcon,
  JPGFileIcon,
  JSFiledIcon,
  MarkDownFileIcon,
  PDFIcon,
  PNGFileIcon,
  PPTFileIcon,
  RawFileIcon,
  SVGFileIcon,
  TIFFFIleIcon,
  TXTFileIcon,
  WordFileIcon,
} from 'components/SVG/fileType';
import PluginStepIcon from 'components/SVG/PluginStepIcon';
import { WarningStatusIcon } from 'components/SVG';

type AttachmentsTypes = 'file' | 'boardNode' | 'memory' | 'workspace';
export type AttachmentCardType = {
  id: string;
  title: string;
  cardType: AttachmentsTypes;
  thumbnail: string;
  actionKey: string;
  fileTypeTag: string;
  file: File | null;
  details: string;
  isUploading: boolean;
  enableDeletion?: boolean;
  iconComponent?: React.ReactNode;
  url?: string;
};

export const fileTypeToIconMap: Record<string, JSX.Element> = {
  pdf: <PDFIcon />,
  md: <MarkDownFileIcon />,
  txt: <TXTFileIcon />,
  tiff: <TIFFFIleIcon />,
  raw: <RawFileIcon />,
  png: <PNGFileIcon />,
  jpg: <JPGFileIcon />,
  jpeg: <JPGFileIcon />,
  js: <JSFiledIcon />,
  docx: <WordFileIcon />,
  xls: <ExcelFileIcon />,
  xlsx: <ExcelFileIcon />,
  ppt: <PPTFileIcon />,
  svg: <SVGFileIcon />,
  memories: <PluginStepIcon />,
  warning: <WarningStatusIcon />,
};

type Props = {
  title: string;
  disabled?: boolean;
  details: string;
  fileTypeTag: string;
  thumbnail: any;
  file: File | null;
  isUploading: boolean;
  cardType: AttachmentsTypes;
  onClose: () => void;
  onClick: () => void;
  isRemovable?: boolean;
  maxWidth?: string;
  minWidth?: string;
  color?: string;
  width?: string;
  height?: string;
  iconComponent?: React.ReactNode;
  isChatAttachment?: boolean;
};

// Helper component for loading indicator
const UploadingOverlay = () => (
  <Box
    position='absolute'
    top={0}
    left={0}
    right={0}
    bottom={0}
    display='flex'
    alignItems='center'
    justifyContent='center'
    bgcolor='rgba(255, 255, 255, 0.5)'
    sx={{ borderRadius: '10px' }}
  >
    <CircularProgress size='24px' />
  </Box>
);

// Component for custom icon display
const IconDisplay = ({
  iconComponent,
  fileTypeTag,
  color,
  width,
  height,
  isUploading,
}: {
  iconComponent?: React.ReactNode;
  fileTypeTag: string;
  file: File | null;
  color?: string;
  width: string;
  height: string;
  isUploading: boolean;
}) => {
  // If custom icon component is provided, render it
  if (iconComponent) {
    return <>{iconComponent}</>;
  }

  // Otherwise render default file type icon
  return (
    <Icon
      sx={{
        width,
        height,
        color,
        position: 'relative',
        '& svg': {
          height: 'inherit',
        },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {(fileTypeTag && fileTypeToIconMap[fileTypeTag?.toLowerCase()]) || (
        <RawFileIcon />
      )}
      {isUploading && <UploadingOverlay />}
    </Icon>
  );
};

// Component for image preview
const ImagePreview = ({
  displayUrl,
  title,
  width,
  height,
  color,
  file,
  fileTypeTag,
  isUploading,
}: {
  displayUrl?: string;
  title: string;
  width: string;
  height: string;
  color?: string;
  file: File | null;
  fileTypeTag: string;
  isUploading: boolean;
}) => (
  <Box
    position='relative'
    display='flex'
    alignItems='center'
    justifyItems='center'
  >
    <SparkImageLoader
      src={displayUrl}
      height={height}
      width={width}
      style={{
        borderRadius: '10px',
        objectFit: 'contain',
        padding: '1px',
      }}
      alt={title}
      fallback={
        <Icon
          sx={{
            width,
            height,
            color,
            '& svg': {
              height: 'inherit',
            },
          }}
        >
          {(file &&
            fileTypeTag &&
            fileTypeToIconMap[fileTypeTag?.toLowerCase()]) || <RawFileIcon />}
        </Icon>
      }
    />
    {isUploading && <UploadingOverlay />}
  </Box>
);

function AttachmentCard({
  title,
  onClose,
  disabled = false,
  onClick,
  cardType,
  thumbnail,
  fileTypeTag,
  details,
  file,
  isUploading,
  isRemovable = true,
  maxWidth = 'fit-content',
  minWidth = 'fit-content',
  color,
  width = '32px',
  height = '32px',
  iconComponent,
  isChatAttachment = true,
}: Props) {
  const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onClose();
  };

  // Check if the file is an image type
  const isImage = React.useMemo(() => {
    if (cardType === 'file' && file instanceof File) {
      return file.type.startsWith('image/');
    }
    return false;
  }, [cardType, file]);

  // Determine if we should use thumbnail
  const shouldUseThumbnail = React.useMemo(() => {
    return cardType === 'boardNode' || (!file && thumbnail);
  }, [cardType, file, thumbnail]);

  // Get preview URL for image files
  const previewUrl = React.useMemo(() => {
    if (shouldUseThumbnail) {
      return thumbnail;
    }

    if (file instanceof File) {
      // For image files, create an object URL for immediate preview
      if (file.type.startsWith('image/')) {
        return URL.createObjectURL(file);
      }
      // For non-image files that should show as images
      if (isImage) {
        const reader = new FileReader();
        return new Promise<string>((resolve) => {
          reader.onload = (e) => {
            if (typeof e.target?.result === 'string') {
              resolve(e.target.result);
            }
          };
          reader.readAsDataURL(file);
        });
      }
    }

    return undefined;
  }, [file, shouldUseThumbnail, thumbnail, isImage]);

  // Track the actual image URL to display
  const [displayUrl, setDisplayUrl] = React.useState<string | undefined>(
    undefined
  );

  // Handle the preview URL resolution and cleanup
  React.useEffect(() => {
    if (previewUrl instanceof Promise) {
      previewUrl.then((url) => setDisplayUrl(url));
    } else {
      setDisplayUrl(previewUrl);
    }

    return () => {
      if (displayUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(displayUrl);
      }
    };
  }, [displayUrl, previewUrl]);

  // Determine what to display based on props
  const showImagePreview =
    !iconComponent && (isImage || shouldUseThumbnail) && isChatAttachment;
  const isDisabled = isUploading || disabled;

  return (
    <Grid position='relative' component='div' width='auto !important'>
      <Grid
        padding='8px'
        border='1px solid lightgray'
        borderRadius='18px'
        gap={0.5}
        container
        minWidth={isImage ? 'fit-content' : minWidth}
        maxWidth={maxWidth}
        alignItems='center'
        flexWrap='nowrap'
        sx={{
          cursor: showImagePreview ? 'pointer' : 'default',
        }}
        onClick={(e) => {
          onClick();
          e.stopPropagation();
        }}
      >
        {/* Display area - either custom icon, image preview, or file type icon */}
        {showImagePreview ? (
          <ImagePreview
            displayUrl={displayUrl}
            title={title}
            width={width}
            height={height}
            color={color}
            file={file}
            fileTypeTag={fileTypeTag}
            isUploading={isUploading}
          />
        ) : (
          <IconDisplay
            iconComponent={iconComponent}
            fileTypeTag={fileTypeTag}
            file={file}
            color={color}
            width={width}
            height={height}
            isUploading={isUploading}
          />
        )}

        {/* Text details */}
        {!isImage && (
          <Box display='flex' flexDirection='column' justifyContent='center'>
            {title && (
              <SparkEllipsisTypography
                fontFamily='Mont-semibold'
                variant='body2'
                sx={{
                  WebkitLineClamp: 1,
                }}
              >
                {title}
              </SparkEllipsisTypography>
            )}
            <SparkEllipsisTypography
              fontFamily='Mont-semibold'
              variant='body2'
              sx={{
                WebkitLineClamp: 1,
              }}
            >
              <Typography variant='body2'>{details}</Typography>
            </SparkEllipsisTypography>
          </Box>
        )}

        {/* Close button */}
        {isRemovable && (
          <Button
            disabled={isDisabled}
            sx={{
              position: 'absolute',
              top: '-5px',
              right: '-5px',
              backgroundColor: isDisabled
                ? 'lightgray'
                : 'var(--color-error-main)',
              color: 'var(--color-white)',
              minWidth: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '1px',
              '&.MuiButton-root:hover': {
                backgroundColor: 'var(--color-error-main)',
              },
              borderRadius: '7px',
            }}
            onClick={handleClose}
          >
            <CloseIcon fontSize='inherit' />
          </Button>
        )}
      </Grid>
    </Grid>
  );
}

export default AttachmentCard;
